// ---------------------------------------------------------------------------
// TemplateProposalExchange — the template proposal, wherever it is shown.
//
// ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
//
// The proposal card used to be reachable from exactly one place: the chat
// rail's message switch (`shell/ide-v2/components/chat-messages.tsx`, `case
// "template-proposal"`). Home's hero composer produced the proposal and then
// HANDED OFF to the rail to show it — appending the message, clearing the
// person's prompt, opening the rail, and staying on Home.
//
// Home now hosts its own conversation and the shell adds no rail there
// (`conversationOwner` in `lib/studio-route-registry.ts`). That handoff would
// therefore strand an Accept/Decline card on a surface that no longer renders
// it, with the prompt that produced it already cleared.
//
// So the card was not moved and it was not copied. The RULE — registry
// lookup, the four states, the double-accept guard, what Accept does and what
// a failed Accept must NOT do — lives here once, and each surface places it
// in its own chrome. That is the same shape as `lib/composer-commit.ts` and
// `hooks/use-message-list-autoscroll.ts`: shared rule, per-surface markup.
//
// ── THE ONE THING THAT LEGITIMATELY DIFFERS ─────────────────────────────────
//
// `onDeclined`. Declining is a fresh free-generation intent, and free
// generation is entered differently on each surface — the rail presents a
// readiness panel as the next message in its thread; Home re-submits through
// its own composer with the intake already confirmed. Both END in
// `startAppGenerationFromPrompt` with the same prompt; only the presentation
// before it differs, because the presentation lives on the surface. Every
// other decision above it is shared, which is why the seam is one callback
// and not a second implementation.
// ---------------------------------------------------------------------------

import { useRef, useState, type ReactElement, type ReactNode } from 'react'

import { useStudioLocale } from '@/i18n'
import { useStudioStore } from '@/store/studio-store'
import { extractQuotedProjectName } from '@/features/chat/lib/extract-quoted-project-name'
import { instantiateTemplateToProject } from '@/features/templates/instantiate-service'
import { TEMPLATE_REGISTRY } from '@/features/templates/registry'
import { TemplateProposalCard } from './template-proposal-card'

/**
 * Which of the four things the exchange is currently showing. Surfaces read
 * this to pick their own chrome (icon, tone, width) and nothing else.
 */
export type TemplateProposalTone =
  | 'pending'
  | 'accepted'
  | 'declined'
  | 'unavailable'

/**
 * The proposal itself. Deliberately the serialisable subset: the chat message
 * carries only `templateId`, never the registry entry (which is not message
 * state), so the entry is looked back up here — on both surfaces, by the same
 * code, against the same live registry.
 */
export interface TemplateProposalContent {
  templateId: string
  displayName: string
  matchedTerms: string[]
  prompt: string
}

export interface TemplateProposalExchangeProps {
  proposal: TemplateProposalContent
  /**
   * The surface's own wrapper. Called with what to show and which state it is
   * in; the surface decides how it looks. The chat rail wraps it in an
   * `AiBubble`, Home in its composer's read-back card frame.
   */
  chrome: (part: { tone: TemplateProposalTone; children: ReactNode }) => ReactElement
  /**
   * Run after a decline has been recorded, with the prompt that produced the
   * proposal. See the header: this is the one seam.
   */
  onDeclined: (prompt: string) => void
  /** Successful creation readback for surfaces that own durable projection. */
  onAccepted?: (result: { projectId: string; prompt: string }) => void
}

type TemplateProposalOutcome =
  | { status: 'pending' }
  | { status: 'accepted'; projectId: string }
  | { status: 'declined' }

export function TemplateProposalExchange({
  proposal,
  chrome,
  onDeclined,
  onAccepted,
}: TemplateProposalExchangeProps) {
  const { strings } = useStudioLocale()
  const copy = strings.chat.templateProposal

  // Post-action state, local to this instance (one exchange per proposal).
  // There is no store-level message-update mechanism, which is why this was
  // MessageRow-local before the extraction and is component-local now.
  //
  // A FAILED accept does NOT transition it: the exchange stays 'pending' so
  // the card — and its 자유 생성 escape hatch — remains interactive, and a
  // `pendingChatNotice` carries the honest failure copy. Nothing was created;
  // saying otherwise would be the worst possible lie for this particular
  // button.
  const [outcome, setOutcome] = useState<TemplateProposalOutcome>({
    status: 'pending',
  })
  // Double-click guard. A second click before the first call's result lands
  // must be a no-op, not a second `instantiateTemplateToProject` (which would
  // create a second project). A ref rather than state because the guard has
  // to be effective on the SECOND click even if it fires before the first
  // click's state update has committed.
  const acceptInFlightRef = useRef(false)
  const [isAccepting, setIsAccepting] = useState(false)

  const entry = TEMPLATE_REGISTRY.find(
    (candidate) => candidate.pkg.templateId === proposal.templateId,
  )

  if (outcome.status === 'accepted') {
    return chrome({ tone: 'accepted', children: copy.accepted })
  }
  if (outcome.status === 'declined') {
    return chrome({ tone: 'declined', children: copy.declined })
  }
  if (!entry) {
    // Defensive: a proposal is only ever raised right after a live
    // `matchTemplates()` hit, but a stale one referencing a templateId no
    // longer in the registry must degrade honestly instead of throwing.
    return chrome({ tone: 'unavailable', children: copy.unavailable })
  }

  return chrome({
    tone: 'pending',
    children: (
      <TemplateProposalCard
        displayName={proposal.displayName}
        matchedTerms={proposal.matchedTerms}
        busy={isAccepting}
        onAccept={() => {
          if (acceptInFlightRef.current) return
          acceptInFlightRef.current = true
          setIsAccepting(true)
          void (async () => {
            const quotedProjectName = extractQuotedProjectName(proposal.prompt)
            const hasAppNameSlot = entry.pkg.slots.some(
              (slot) => slot.slotId === 'app-name',
            )
            try {
              const result = await instantiateTemplateToProject(entry, {
                projectName: quotedProjectName ?? entry.displayName,
                slotValues:
                  quotedProjectName && hasAppNameSlot
                    ? { 'app-name': quotedProjectName }
                    : {},
              })
              if (result.ok) {
                setOutcome({ status: 'accepted', projectId: result.projectId })
                // This callback projects an already-confirmed success. It is
                // deliberately best-effort: local persistence denial must not
                // turn a project the server created into a failed creation or
                // re-enter the one-shot accept path.
                try {
                  onAccepted?.({
                    projectId: result.projectId,
                    prompt: proposal.prompt,
                  })
                } catch (callbackError) {
                  console.error(
                    '[template-proposal] accepted projection failed:',
                    callbackError,
                  )
                }
                await useStudioStore.getState().openServerProject(result.projectId)
                return
              }
              useStudioStore.getState().setPendingChatNotice(copy.failure)
            } catch (err) {
              // CARRIED REVIEW FINDING: `api.createProject` rejections bubble
              // OUTSIDE `instantiateTemplateToProject`'s typed result union
              // (see the JSDoc caveat on InstantiateToProjectResult) — treat a
              // thrown rejection identically to an `{ ok: false }` result.
              console.error(
                '[template-proposal] accept failed:',
                err,
              )
              useStudioStore.getState().setPendingChatNotice(copy.failure)
            } finally {
              acceptInFlightRef.current = false
              setIsAccepting(false)
            }
          })()
        }}
        onDecline={() => {
          setOutcome({ status: 'declined' })
          onDeclined(proposal.prompt)
        }}
      />
    ),
  })
}
